import type { ChatContact, ChatMember, ChatMessage, ChatPerson, ChatRoom as ApiChatRoom, ChatRoomKind } from "@shyne/shared/api";
import { ChatRoom, ChatRoomMessage, ChatRoomType, type Contact } from "@/models/chat.model.ts";
import { organizationTypeOfKind } from "@/repositories/organization-records";
import type { UserMini } from "@/lib/store/users.slice.ts";
import { rowStatusOf } from "@/repositories/appointment-records";

const ROOM_TYPE_OF_KIND: Record<ChatRoomKind, ChatRoomType> = {
  appointment: ChatRoomType.appointment,
  direct: ChatRoomType.direct,
  contract: ChatRoomType.daily_work,
};

export const toChatMessage = (message: ChatMessage): ChatRoomMessage => ({
  id: message.id,
  room: message.room,
  sender: message.sender.id,
  message: message.text,
  is_file: message.isFile,
  file_type: message.fileKind,
  is_system: message.isSystem,
  meta_data: message.metaData,
  created_at: message.sentAt,
  updated_at: message.updatedAt,
});

export const toChatRoom = (room: ApiChatRoom): ChatRoom => ({
  id: room.id,
  type: ROOM_TYPE_OF_KIND[room.kind],
  appointment: room.appointment?.id ?? null,
  daily_work: room.contract?.id ?? null,
  users: room.memberIds,
  updated_at: room.updatedAt,
  unread_count: room.unreadCount,
  last_message_data: room.lastMessage === null ? [] : [toChatMessage(room.lastMessage)],
  ...(room.appointment === null
    ? {}
    : {
        appointment_data: {
          id: room.appointment.id,
          status: rowStatusOf(room.appointment.status),
          date: room.appointment.date,
          property: { name: room.appointment.propertyName },
          unit: { unit_number: room.appointment.unitNumber ?? "" },
          service: { id: room.appointment.service.id, name: room.appointment.service.name },
          vendor: { id: room.appointment.vendor.id, display_name: room.appointment.vendor.name },
        },
      }),
  ...(room.contract === null ? {} : { daily_work_data: { id: room.contract.id, property: { id: room.contract.property.id, name: room.contract.property.name } } }),
});

const namedPerson = (person: ChatPerson): UserMini[] => [{ id: person.id, name: person.name ?? "", image: person.picture ?? undefined }];

export const peopleOfRooms = (rooms: readonly ApiChatRoom[]): UserMini[] =>
  rooms.flatMap((room) => [...(room.directWith === null ? [] : namedPerson(room.directWith)), ...(room.lastMessage === null ? [] : namedPerson(room.lastMessage.sender))]);

export const sendersOf = (messages: readonly ChatMessage[]): UserMini[] => messages.flatMap((message) => namedPerson(message.sender));

export const peopleOfMembers = (members: readonly ChatMember[]): UserMini[] => members.flatMap(namedPerson);

export const toContact = (contact: ChatContact): Contact => ({
  id: contact.id,
  name: contact.name,
  avatar: contact.picture,
  organizationId: contact.organization.id,
  organizationName: contact.organization.name,
  organizationType: organizationTypeOfKind(contact.organization.kind),
});
